import {
  CheckCircleFilled,
  FileExcelOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import {
  Button,
  Collapse,
  Select,
  Skeleton,
  Spin,
  Tag,
} from '@surecompanies/core_components'
import ContentSection from 'layouts/ContentSection/ContentSection'
import React, { useCallback, useRef, useState } from 'react'
import { useBenchmarkSnapshotOptions } from '../hooks/useBenchmarkSnapshotOptions'
import { useUploadAndPopulateDemoQuote } from '../hooks/useUploadAndPopulateDemoQuote'
import { DemoPopulationResult } from '../types'
import QuoteFileDropzone from './QuoteFileDropzone'

interface QuoteFileUploadSectionProps {
  /** ID of the demo customer — passed to the upload/populate calls. */
  customerId: string
  /**
   * Payroll cycle from the customer's enrollment config, forwarded to the
   * populate endpoint so the backend uses the correct pay-period value.
   */
  payrollCycle?: string
  /**
   * Whether the enrollment configuration has loaded yet. While true the
   * section shows a skeleton instead of the upload form.
   */
  enrollmentLoading?: boolean
  /**
   * Whether a valid enrollment configuration record exists. When false the
   * upload is disabled and a note is shown — the backend's populate endpoint
   * reads the enrollment window and will fail without one.
   */
  hasEnrollment?: boolean
  /**
   * Total number of employees seeded into this demo customer, derived from
   * the member search endpoint. When > 0 the section shows the persisted
   * "populated" state instead of the upload form, surviving page reloads.
   */
  employeeCount?: number
  /**
   * Whether the population status query is still in flight. Shows a skeleton
   * while true so the section doesn't flash the upload form before resolving.
   */
  populationLoading?: boolean
  /** Called once populate succeeds, with the population result. */
  onPopulated?: (result: DemoPopulationResult) => void
}

/**
 * Standalone "Quote File" section for the demo customer view page.
 *
 * Implements the three-step presigned S3 flow:
 *   1. POST .../presigned-upload-url
 *   2. raw fetch PUT to S3
 *   3. POST .../populate
 *
 * Population is one-shot (the backend returns 409 on a second attempt),
 * so there is no "Replace file" affordance — users must create a new demo
 * customer to re-populate.
 */
export default function QuoteFileUploadSection({
  customerId,
  payrollCycle,
  enrollmentLoading = false,
  hasEnrollment = true,
  employeeCount = 0,
  populationLoading = false,
  onPopulated,
}: QuoteFileUploadSectionProps) {
  // File selected in the dropzone but not yet submitted.
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  // The file that was actually passed to handleFile — frozen at submit time so
  // the success tag always reflects what was uploaded, even if the user somehow
  // swaps the dropzone selection before the async flow completes.
  const submittedFileRef = useRef<File | null>(null)
  // Undefined until the admin picks one, so the preferred default applies
  // once the snapshot list has loaded.
  const [pickedSnapshotId, setPickedSnapshotId] = useState<
    number | null | undefined
  >(undefined)

  const {
    options: snapshotOptions,
    defaultSnapshotId,
    isLoading: snapshotsLoading,
  } = useBenchmarkSnapshotOptions()
  const benchmarkSnapshotId =
    pickedSnapshotId === undefined ? defaultSnapshotId : pickedSnapshotId

  const { uploadState, result, inlineError, handleFile } =
    useUploadAndPopulateDemoQuote({
      customerId,
      payrollCycle,
      benchmarkSnapshotId,
      onPopulated,
    })

  const isWorking =
    uploadState === 'presigning' ||
    uploadState === 'uploading' ||
    uploadState === 'populating' ||
    uploadState === 'confirming'

  const progressLabel = (() => {
    if (uploadState === 'presigning') return 'Preparing upload…'
    if (uploadState === 'uploading') return 'Uploading quote file…'
    if (uploadState === 'populating') return 'Creating employees…'
    if (uploadState === 'confirming') return 'Waiting for employees to appear…'
    return 'Upload & populate'
  })()

  const handleFileChange = useCallback((file: File) => {
    setPendingFile(file)
  }, [])

  const handleUploadClick = useCallback(async () => {
    if (!pendingFile) return
    // Capture the file reference before the async flow so the success tag
    // is guaranteed to reflect what was actually submitted.
    submittedFileRef.current = pendingFile
    await handleFile(pendingFile)
  }, [pendingFile, handleFile])

  const skipped = result?.skipped ?? []
  const errors = result?.errors ?? []
  const hasSkipped =
    result !== null && (skipped.length > 0 || errors.length > 0)

  return (
    <ContentSection title="Quote File">
      {uploadState === 'success' && result !== null ? (
        /* ── Population complete ── */
        <Stack space={12}>
          <Stack direction="row" space={8} align="center">
            <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
            <Typography size={14} style={{ fontWeight: 600, color: '#262626' }}>
              {result.employeesCreated} employee
              {result.employeesCreated !== 1 ? 's' : ''} created
            </Typography>
          </Stack>

          {submittedFileRef.current && (
            <Tag
              color="green"
              icon={<FileExcelOutlined />}
              style={{
                fontSize: 13,
                padding: '4px 10px',
                width: 'fit-content',
              }}
            >
              {submittedFileRef.current.name}
            </Tag>
          )}

          {hasSkipped && (
            <Collapse
              size="small"
              items={[
                {
                  key: 'details',
                  label: (
                    <Stack direction="row" space={6} align="center">
                      <WarningOutlined style={{ color: '#faad14' }} />
                      <Typography size={13}>
                        {skipped.length} skipped
                        {errors.length > 0
                          ? `, ${errors.length} error${errors.length !== 1 ? 's' : ''}`
                          : ''}
                      </Typography>
                    </Stack>
                  ),
                  children: (
                    <Stack space={8}>
                      {errors.length > 0 && (
                        <Stack space={4}>
                          <Typography
                            size={12}
                            style={{ fontWeight: 600, color: '#cf1322' }}
                          >
                            Errors
                          </Typography>
                          {errors.map((e, i) => (
                            <Typography key={i} size={12} color="#595959">
                              {e}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                      {skipped.length > 0 && (
                        <Stack space={4}>
                          <Typography
                            size={12}
                            style={{ fontWeight: 600, color: '#874d00' }}
                          >
                            Skipped rows
                          </Typography>
                          {skipped.map((row, i) => (
                            <Typography key={i} size={12} color="#595959">
                              Employee {row.employeeId} ({row.relationship}) —{' '}
                              {row.reason.replace(/_/g, ' ')}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  ),
                },
              ]}
            />
          )}
        </Stack>
      ) : isWorking ? (
        /* ── Upload or confirm in progress ── */
        <Stack direction="row" space={10} align="center">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
          <Typography size={13} color="#595959">
            {progressLabel}
          </Typography>
        </Stack>
      ) : populationLoading ? (
        /* ── Population status query in flight ── */
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : employeeCount > 0 ? (
        /* ── Persisted populated state (survives page reload) ── */
        <Stack space={8}>
          <Stack direction="row" space={8} align="center">
            <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
            <Typography size={14} style={{ fontWeight: 600, color: '#262626' }}>
              {employeeCount} employee{employeeCount !== 1 ? 's' : ''} populated
            </Typography>
          </Stack>
          <Typography size={12} color="#8c8c8c">
            Employee population is complete. To re-run, create a new demo
            customer.
          </Typography>
        </Stack>
      ) : enrollmentLoading ? (
        /* ── Enrollment config still loading ── */
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : !hasEnrollment ? (
        /* ── No enrollment config — upload blocked ── */
        <Stack direction="row" space={8} align="flex-start">
          <InfoCircleOutlined
            style={{
              color: '#8c8c8c',
              fontSize: 16,
              marginTop: 2,
              flexShrink: 0,
            }}
          />
          <Typography size={13} color="#595959">
            An enrollment period must be configured before the quote file can be
            uploaded. Set the benefit effective and enrollment dates first, then
            return here.
          </Typography>
        </Stack>
      ) : (
        /* ── Upload form ── */
        <Stack space={12}>
          <Typography size={13} color="#595959">
            Upload the prospect quote .xlsm file. The "Shoppable" tab will be
            used to seed the employee population — PHI is sanitized before
            import. This can only be done once per demo customer.
          </Typography>

          <QuoteFileDropzone
            file={pendingFile}
            onChange={handleFileChange}
            error={inlineError ?? undefined}
          />

          {pendingFile && (
            <Stack space={4}>
              <Typography size={13} color="#595959">
                Benchmark plans source
              </Typography>
              <Select
                aria-label="Benchmark plans source"
                options={snapshotOptions}
                value={benchmarkSnapshotId}
                onChange={(value: number | null) => setPickedSnapshotId(value)}
                loading={snapshotsLoading}
                disabled={isWorking || snapshotsLoading}
                style={{ maxWidth: 360 }}
              />
            </Stack>
          )}

          {pendingFile && (
            <div>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                disabled={snapshotsLoading}
                onClick={handleUploadClick}
              >
                Upload & populate
              </Button>
            </div>
          )}
        </Stack>
      )}
    </ContentSection>
  )
}
